// Decides which pages should remain rendered. Pure function — knows nothing
// about bitmaps, canvases, or scheduling.

// On narrow viewports (phones), fewer off-screen pages are worth the memory.
const COMPACT_VIEWPORT_MAX_WIDTH_PX = 480
const COMPACT_BUFFER_BEFORE = 1
const COMPACT_BUFFER_AFTER = 1
// The default buffer is asymmetric: users scroll down more than up, so we
// pre-render more pages after the visible region than before it.
const DEFAULT_BUFFER_BEFORE = 2
const DEFAULT_BUFFER_AFTER = 3

function normalizePageNumbers(pageNumbers: number[]) {
  return [...new Set(pageNumbers)].sort((a, b) => a - b)
}

function resolveBufferedWindow({
  firstVisiblePageNumber,
  lastVisiblePageNumber,
  isCompactViewport
}: {
  firstVisiblePageNumber: number
  lastVisiblePageNumber: number
  isCompactViewport: boolean
}) {
  const windowMin = firstVisiblePageNumber - (isCompactViewport ? COMPACT_BUFFER_BEFORE : DEFAULT_BUFFER_BEFORE)
  const windowMax = lastVisiblePageNumber + (isCompactViewport ? COMPACT_BUFFER_AFTER : DEFAULT_BUFFER_AFTER)

  return {
    windowMin,
    windowMax
  }
}

export function resolveRenderPlan(
  visiblePageNumbers: number[],
  pageNumbers: number[],
  viewportWidthPx: number
) {
  if (pageNumbers.length == 0 || visiblePageNumbers.length == 0) {
    return []
  }
  const normalizedPageNumbers = normalizePageNumbers(pageNumbers)
  const pageNumberSet = new Set(normalizedPageNumbers)
  const normalizedVisiblePageNumbers = normalizePageNumbers(visiblePageNumbers).filter((pageNumber) =>
    pageNumberSet.has(pageNumber)
  )

  if (normalizedVisiblePageNumbers.length == 0) {
    return []
  }

  const isCompactViewport = viewportWidthPx <= COMPACT_VIEWPORT_MAX_WIDTH_PX
  const { windowMin, windowMax } = resolveBufferedWindow({
    firstVisiblePageNumber: normalizedVisiblePageNumbers[0],
    lastVisiblePageNumber:
      normalizedVisiblePageNumbers[normalizedVisiblePageNumbers.length - 1],
    isCompactViewport
  })

  return normalizedPageNumbers.filter((pageNumber) => {
    return pageNumber >= windowMin && pageNumber <= windowMax
  })
}
